import React, { useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import BreadCrumb from "components/common/BreadCrumb";
import { DateTime } from "components/common/Date";
import TableContainer from "components/common/TableContainer";
import Avatar from "components/common/Avatar";
import { getDocumentTitle } from "utils/helpers";
import { ActivityLog, ActivityLogModule } from "./types/activity-log.types";
import { getActivityLogs } from "./services/ActivityLogService";
import {
  ACTIVITY_LOG_MODULE_COLOR,
  ACTIVITY_LOG_MODULE_LABEL,
  ACTIVITY_LOG_SOURCE_ROUTE,
} from "./constants/activity-log.constants";

function formatStatus(s: string | null): string {
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveSourceRoute(
  module: string,
  sourceId: number | null,
): string | null {
  if (!sourceId) return null;
  const base = ACTIVITY_LOG_SOURCE_ROUTE[module as ActivityLogModule];
  return base ? `${base}/${sourceId}` : null;
}

function getSourceCode(
  module: string,
  source: Record<string, unknown> | null,
): string | null {
  if (!source) return null;
  if (module === ActivityLogModule.AUTH) {
    const name = [source.firstName, source.lastName].filter(Boolean).join(" ");
    return name || (source.email as string) || null;
  }
  return (
    (source.code as string) ??
    (source.title as string) ??
    (source.name as string) ??
    null
  );
}

const ActivityLogPage = () => {
  document.title = getDocumentTitle("Activity Log");

  const [moduleFilter, setModuleFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<Date[]>([]);

  const toISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const requestParams = useMemo(
    () => ({
      module: moduleFilter || undefined,
      fromDate: dateRange[0] ? toISODate(dateRange[0]) : undefined,
      toDate: dateRange[1] ? toISODate(dateRange[1]) : undefined,
    }),
    [moduleFilter, dateRange],
  );

  const columns: ColumnDef<ActivityLog>[] = useMemo(
    () => [
      {
        header: "Actor",
        accessorKey: "actorUserId",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const actor = row.original.actor;
          if (!actor) {
            return (
              <div className="d-flex align-items-center gap-2">
                <div className="avatar-xs flex-shrink-0">
                  <span className="avatar-title rounded-circle bg-secondary-subtle text-secondary fs-14">
                    <i className="ri-robot-line" />
                  </span>
                </div>
                <span className="text-muted small">System</span>
              </div>
            );
          }
          const name = `${actor.firstName} ${actor.lastName}`.trim();
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-xs flex-shrink-0">
                <Avatar name={name} image={actor.avatar} />
              </div>
              <span className="fw-medium text-body small">{name}</span>
            </div>
          );
        },
      },
      {
        header: "Module",
        accessorKey: "module",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const mod = row.original.module as ActivityLogModule;
          const label = ACTIVITY_LOG_MODULE_LABEL[mod] ?? mod;
          const color = ACTIVITY_LOG_MODULE_COLOR[mod] ?? "secondary";
          return (
            <Badge color={color} className="fw-normal">
              {label}
            </Badge>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { fromStatus, status } = row.original;
          return (
            <div className="d-flex align-items-center gap-1 small">
              {fromStatus ? (
                <>
                  <span className="text-muted">{formatStatus(fromStatus)}</span>
                  <i className="ri-arrow-right-line text-muted" />
                </>
              ) : null}
              <span className="fw-medium text-body">
                {formatStatus(status)}
              </span>
            </div>
          );
        },
      },
      {
        header: "Source",
        accessorKey: "sourceId",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { module, sourceId, source } = row.original;
          const code = getSourceCode(module, source);
          const route = resolveSourceRoute(module, sourceId);
          if (!code) return <span className="text-muted small">—</span>;
          return route ? (
            <Link to={route} className="small fw-medium link-primary">
              {code}
            </Link>
          ) : (
            <span className="small fw-medium">{code}</span>
          );
        },
      },
      {
        header: "Message",
        accessorKey: "message",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.message ? (
            <span className="small text-muted">{row.original.message}</span>
          ) : (
            <span className="text-muted small">—</span>
          ),
      },
      {
        header: "Time",
        accessorKey: "createdAt",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
    ],
    [],
  );

  const clearFilters = () => {
    setModuleFilter("");
    setDateRange([]);
  };

  const filterCount = [moduleFilter, dateRange.length > 0].filter(Boolean).length;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Activity Log" pageTitle="System" />
        <Card>
          <CardHeader className="d-flex justify-content-between">
            <h5 className="card-title mb-0">Activity Log</h5>
          </CardHeader>
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              fetchApi={getActivityLogs}
              requestParams={requestParams}
              pageSize={10}
              globalSearch={true}
              divClass="table-responsive table-card mb-3"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
              toolbarRight={
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Input
                    type="select"
                    className="form-select"
                    style={{ width: 170 }}
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                  >
                    <option value="">All modules</option>
                    {Object.values(ActivityLogModule).map((m) => (
                      <option key={m} value={m}>
                        {ACTIVITY_LOG_MODULE_LABEL[m]}
                      </option>
                    ))}
                  </Input>
                  <div style={{ width: 220 }}>
                    <Flatpickr
                      className="form-control"
                      placeholder="Date range"
                      value={dateRange}
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                        altInput: true,
                        altFormat: "d M, Y",
                        altInputClass: "form-control",
                      }}
                      onChange={(dates) => setDateRange(dates)}
                    />
                  </div>
                  {filterCount > 0 && (
                    <button
                      type="button"
                      className="btn btn-soft-danger d-flex align-items-center gap-1"
                      onClick={clearFilters}
                    >
                      <i className="ri-close-circle-line" />
                      Clear
                    </button>
                  )}
                </div>
              }
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ActivityLogPage;
